// $lib/state/lookups.svelte.ts
// Global lookups state for Svelte 5 app
import { getFirestore } from 'firebase/firestore';
import { browser } from '$app/environment';
import { readDoc } from '$lib/firestore/cachedReads';
import type { Staff, SectionInfo, SchoolDay, SchoolDays, TermLookup } from '../types';
import { computeSchoolYear, currentSchoolYear } from '../../../../shared/src/schemas/grades.js';

// Define the shape of lookups
export type Lookups = {
	staff: Record<string, Staff>;
	sections: Record<string, SectionInfo>;
	schoolDays: SchoolDays;
	futureSchoolDays?: SchoolDay[];
	pastSchoolDays?: SchoolDay[];
	/** Term dates, keyed by marking period id. See shared/src/schemas/terms.ts. */
	terms: TermLookup;
	/**
	 * True once a load has completed, whatever it found. Distinguishes "no terms
	 * imported" from "not loaded yet" -- callers fall back to a calendar guess in
	 * the first case and should wait in the second, and an empty map alone cannot
	 * tell them apart.
	 */
	termsLoaded?: boolean;
};

// Deeply reactive state for lookups
export const lookups = $state<Lookups>({
	staff: {},
	sections: {},
	schoolDays: {},
	futureSchoolDays: [],
	pastSchoolDays: [],
	terms: {},
	termsLoaded: false
});

// Cache helper: Check if data is fresh (loaded after 4am Eastern today)
function isFreshEnough(key: string): boolean {
	// SSR guard: localStorage only exists in browser
	if (!browser) return false;

	const lastLoaded = localStorage.getItem(`lookup_${key}_timestamp`);
	if (!lastLoaded) return false;

	const lastLoadedDate = new Date(parseInt(lastLoaded));

	// Get current time in Eastern timezone
	const nowEastern = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
	const now = new Date(nowEastern);

	// Today at 4am Eastern
	const today4amEastern = new Date(nowEastern);
	today4amEastern.setHours(4, 0, 0, 0);

	// If it's before 4am Eastern, use yesterday's 4am as cutoff
	if (now < today4amEastern) {
		today4amEastern.setDate(today4amEastern.getDate() - 1);
	}

	return lastLoadedDate >= today4amEastern;
}

/**
 * Exported for mock/test seed helpers (see `$lib/state/mockHelpers`): seeding
 * `lookups.*` directly puts data in memory, but the various `load*` functions
 * above only skip their Firestore round trip when the *freshness* timestamp
 * says so too. Without this, a story or test that seeds `lookups.sections`
 * and then mounts a component that calls `getSectionsForAdminSchool()` (which
 * awaits `loadSections()` unconditionally) triggers a real `getDoc()` against
 * whatever project is configured -- there is no Firestore emulator or mock
 * backend in that environment, so the call hangs well past a `waitFor`'s
 * default 1s timeout instead of failing fast.
 */
export function markAsLoaded(key: string) {
	// SSR guard: localStorage only exists in browser
	if (!browser) return;
	localStorage.setItem(`lookup_${key}_timestamp`, Date.now().toString());
}

export async function loadSchoolDays(force = false) {
	// Skip if data is fresh enough AND we have data in memory
	const hasData = Object.keys(lookups.schoolDays).length > 0;
	if (!force && hasData && isFreshEnough('school_days')) {
		console.log('📦 Using cached school days (fresh enough, in memory)');
		return;
	}

	try {
		const db = getFirestore();
		const { doc } = await import('firebase/firestore');
		const ref = doc(db, 'lookups', 'school_days');
		const snap = await readDoc(ref, {
			isFresh: isFreshEnough('school_days'),
			force,
			onServerRead: () => markAsLoaded('school_days')
		});
		if (snap.exists()) {
			lookups.schoolDays = snap.data() as SchoolDays;
			// Compute convenience arrays
			const allDays = Object.values(lookups.schoolDays) as SchoolDay[];
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			lookups.futureSchoolDays = allDays
				.filter((d) => d.in_session && new Date(d.date) >= today)
				.sort((a, b) => a.date.localeCompare(b.date));
			const yesterday = new Date(today);
			yesterday.setDate(today.getDate() - 1);
			// Scoped to the current school year, not to all of history.
			// `lookups/school_days` is a merge write that accumulates every year
			// forever, and this array is the denominator for every attendance
			// percentage and the day grid on the roster. Unscoped, in August it
			// held last June's days: a student with no attendance records yet
			// divided zero absences by ~181 days from a year they weren't being
			// measured on, and read as 100% present.
			const thisYear = currentSchoolYear();
			lookups.pastSchoolDays = allDays
				.filter(
					(d) =>
						d.in_session && new Date(d.date) <= yesterday && computeSchoolYear(d.date) === thisYear
				)
				.sort((a, b) => a.date.localeCompare(b.date));
			console.log(
				'Loaded school days:',
				allDays.length,
				'future:',
				lookups.futureSchoolDays.length,
				'past:',
				lookups.pastSchoolDays.length
			);
		} else {
			lookups.schoolDays = {};
			lookups.futureSchoolDays = [];
			lookups.pastSchoolDays = [];
			console.warn('No school days found in lookups/school_days');
		}
	} catch (err) {
		console.error('Failed to load school days:', err);
		lookups.futureSchoolDays = [];
		lookups.pastSchoolDays = [];
	}
}

/**
 * Loads term dates into `lookups.terms`.
 *
 * One document, cached like every other lookup. An absent document is not an
 * error: `marking_periods.csv` is declared in the import config but nothing
 * confirms the school's export actually delivers it, and every caller keeps a
 * fallback for that case. `termsLoaded` is set either way so callers can tell
 * "nothing imported" from "not asked yet".
 */
export async function loadTerms(force = false) {
	const hasData = Object.keys(lookups.terms).length > 0;
	if (!force && hasData && isFreshEnough('marking_periods')) {
		console.log('📦 Using cached terms (fresh enough, in memory)');
		return;
	}

	try {
		const db = getFirestore();
		const { doc } = await import('firebase/firestore');
		const ref = doc(db, 'lookups', 'marking_periods');
		const snap = await readDoc(ref, {
			isFresh: isFreshEnough('marking_periods'),
			force,
			onServerRead: () => markAsLoaded('marking_periods')
		});
		if (snap.exists()) {
			lookups.terms = snap.data() as TermLookup;
			console.log(`Loaded ${Object.keys(lookups.terms).length} terms`);
		} else {
			lookups.terms = {};
			console.warn(
				'No lookups/marking_periods document. Term dates are unavailable; ' +
					'callers will fall back. Check whether marking_periods.csv is being delivered.'
			);
		}
	} catch (err) {
		// A denied or failed read must not take down a page that only wanted a
		// default tab: leave terms empty and let the caller's fallback run.
		console.error('Failed to load terms:', err);
		lookups.terms = {};
	} finally {
		lookups.termsLoaded = true;
	}
}

// Loads all staff into lookups.staff
export async function loadStaff(force = false) {
	// Skip if data is fresh enough AND we have data in memory
	const hasData = Object.keys(lookups.staff).length > 0;
	if (!force && hasData && isFreshEnough('staff')) {
		console.log('📦 Using cached staff (fresh enough, in memory)');
		return;
	}

	try {
		const db = getFirestore();
		const { doc } = await import('firebase/firestore');
		const ref = doc(db, 'lookups', 'staff');
		const snap = await readDoc(ref, {
			isFresh: isFreshEnough('staff'),
			force,
			onServerRead: () => markAsLoaded('staff')
		});
		if (snap.exists()) {
			lookups.staff = snap.data() as Record<string, Staff>;
			console.log(`Loaded ${Object.keys(lookups.staff).length} staff`);
		} else {
			lookups.staff = {};
			console.warn('No staff lookup found in lookups/staff');
		}
	} catch (err) {
		console.error('Failed to load staff:', err);
		// Optionally: set an error state or notify the user here
	}
}

// Loads all sections into lookups.sections
export async function loadSections(force = false) {
	// Skip if data is fresh enough AND we have data in memory
	const hasData = Object.keys(lookups.sections).length > 0;
	if (!force && hasData && isFreshEnough('sections')) {
		console.log('📦 Using cached sections (fresh enough, in memory)');
		return;
	}

	try {
		const db = getFirestore();
		const { doc } = await import('firebase/firestore');
		const ref = doc(db, 'lookups', 'sections');
		const snap = await readDoc(ref, {
			isFresh: isFreshEnough('sections'),
			force,
			onServerRead: () => markAsLoaded('sections')
		});
		if (snap.exists()) {
			// The doc data is an object: { [section_id]: SectionInfo }
			lookups.sections = snap.data() as Record<string, SectionInfo>;
			console.log(
				'Loaded sections:',
				Object.keys(lookups.sections).length,
				$state.snapshot(lookups.sections)
			);
		} else {
			lookups.sections = {};
			console.warn('No sections found in lookups/sections');
		}
	} catch (err) {
		console.error('Failed to load sections:', err);
	}
}

/**
 * Clear all lookup caches (useful for debugging)
 */
export function clearLookupCache() {
	lookups.staff = {};
	lookups.sections = {};
	lookups.schoolDays = {};
	lookups.futureSchoolDays = [];
	lookups.pastSchoolDays = [];

	if (browser) {
		// Clear timestamps only - Firestore's IndexedDB cache persists
		localStorage.removeItem('lookup_staff_timestamp');
		localStorage.removeItem('lookup_sections_timestamp');
		localStorage.removeItem('lookup_school_days_timestamp');
	}

	console.log('🗑️ Lookup cache cleared (next load will fetch from Firestore IndexedDB cache)');
}

// Add similar loaders for other lookups as needed
//
// For "which staff belong in a picker", use `isSelectableStaff` from
// `$lib/utils/search` against `lookups.staff` — the one selectability rule
// every staff picker in the app shares (Impersonate, the intervention plan
// builder, StaffPicker).
